import React, { useRef } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useLandingTheme } from "../LandingThemeContext";
import { DeferredImage } from "../visuals/DeferredImage";
import { useIsNearViewport } from "../visuals/useIsNearViewport";

// The names are the logos' alt text. They are the universities' official names, so they are the
// same on the Spanish page (not part of landingCopy.ts).
// Explicit greyed originals are both sharper and smaller than their resized copies.
const UNIVERSITY_LOGOS: Array<{ file: string; greyedFile?: string; name: string }> = [
  { file: "harvard-w272.webp", greyedFile: "harvard.webp", name: "Harvard University" },
  { file: "yale-w272.webp", name: "Yale University" },
  { file: "sgu-w272.webp", name: "St. George University" },
  { file: "columbia-w272.webp", name: "Columbia University" },
  { file: "princeton-w272.webp", greyedFile: "princeton.webp", name: "Princeton University" },
  { file: "penn-w272.webp", name: "University of Pennsylvania" },
  { file: "cornell-w272.webp", greyedFile: "cornell.webp", name: "Cornell University" },
  { file: "brown-w272.webp", greyedFile: "brown.webp", name: "Brown University" },
  { file: "umich-w272.webp", greyedFile: "umich.webp", name: "University of Michigan" },
  { file: "berkeley-w272.webp", name: "UC Berkeley" },
  { file: "texas-w272.webp", greyedFile: "texas.webp", name: "University of Texas" },
  { file: "unsw-w272.webp", name: "UNSW" },
  { file: "paris-w272.webp", greyedFile: "paris.webp", name: "University of Paris" },
  { file: "dartmouth-w272.webp", greyedFile: "dartmouth.webp", name: "Dartmouth College" },
  { file: "stanford-w272.webp", greyedFile: "stanford.webp", name: "Stanford University" },
];

const scrollLeftByHalfTrack = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

// `greyed` swaps in pre-processed copies of the logos (grey ink on a transparent background, see
// public/images/landing/universities-greyed) so they sit quietly on the page with no white boxes. A
// CSS blend can't do this here: the animated track is its own stacking context, which isolates blending.
export default function UniversityMarquee({ greyed = false }: { greyed?: boolean }) {
  const theme = useLandingTheme();
  // The strip is far below the fold: its 30 logos load once it comes near, not with the first paint.
  const marqueeRef = useRef<HTMLDivElement>(null);
  const isMarqueeNearViewport = useIsNearViewport(marqueeRef);
  // The track is rendered twice so the -50% translate loops seamlessly.
  const logosRenderedTwiceForSeamlessLoop = [...UNIVERSITY_LOGOS, ...UNIVERSITY_LOGOS];
  return (
    <Box
      ref={marqueeRef}
      as="section"
      dir="ltr"
      overflow="hidden"
      py="12px"
      w="100%"
      mt={{ base: "100px", md: "40px", lg: "90px" }}
      // The logos are dark-on-white, so dark themes carry them on a light band.
      bg={theme.isDark ? theme.color.heading : "transparent"}
    >
      <Flex
        w="max-content"
        gap="20px"
        animation={`${scrollLeftByHalfTrack} 45s linear infinite`}
        sx={{
          // Pausing under the pointer is for mice only: on a touch screen a tap leaves the strip "hovered"
          // (and so frozen) until the next tap elsewhere.
          "@media (hover: hover)": { "&:hover": { animationPlayState: "paused" } },
          // The strip always drifts (with it still, a phone shows two and a half logos and nothing says there
          // are more); for visitors who ask for less motion it drifts at half the speed.
          "@media (prefers-reduced-motion: reduce)": { animationDuration: "90s" },
        }}
      >
        {logosRenderedTwiceForSeamlessLoop.map((universityLogo, logoIndex) => {
          const directory = greyed ? "universities-greyed" : "universities";
          const file = greyed ? (universityLogo.greyedFile ?? universityLogo.file) : universityLogo.file;
          const source = `/images/landing/${directory}/${file}`;
          return (
            <DeferredImage
              key={`${universityLogo.file}-${logoIndex}`}
              shouldLoad={isMarqueeNearViewport}
              // The box below is fixed (object-fit: contain), so the placeholder's proportions do not matter.
              fileWidth={136}
              fileHeight={76}
              loading="lazy"
              src={source}
              // Keep the compact 2x copy on ordinary displays; retain the original's
              // fine seal/lettering detail on denser screens.
              srcSet={file.includes("-w272")
                ? `${source} 2x, ${source.replace("-w272", "")} 3x`
                : undefined}
              alt={universityLogo.name}
              w="136px"
              h="76px"
              objectFit="contain"
              flexShrink={0}
              mixBlendMode={theme.isDark ? "multiply" : "normal"}
              opacity={greyed ? 0.7 : 1}
            />
          );
        })}
      </Flex>
    </Box>
  );
}
